import { Grid } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useRifm } from 'rifm';
import { IBRMInputDataRequiredInput } from 'src/common/api/brm/brm-utils';
import BaseButton from 'src/common/components/base-button';
import { getFormattedDate, isDateValid } from 'src/common/helpers/date-helper';
import { IButton } from 'src/common/interfaces/IButton';
import Keypad from 'src/keypad/components/keypad';
import KeypadInputField from 'src/keypad/components/keypad-input-field/keypad-input-field';
import { RifmOptions } from 'src/keypad/keypad-operation';
import { styles } from './date-input-styles';
import { useTranslation } from 'react-i18next';

const DateInput: React.FC<IDateInputProps> = (props: IDateInputProps): JSX.Element => {
  const classes = styles();
  const [canApprove, setCanApprove] = useState<boolean>(false);
  const [keypadInput, setKeypadInput] = useState<string>('');
  const { t } = useTranslation();
  const rifmOptions = RifmOptions.date;
  const rifm = useRifm(rifmOptions(keypadInput, setKeypadInput));

  const { caption, message, isDateOfBirth, onAccept, onCancel, inputName, inputLabel } = props;

  useEffect(() => {
    if (isDateValid(keypadInput)) {
      setCanApprove(true);
    } else {
      setCanApprove(false);
    }
  }, [keypadInput, isDateOfBirth]);

  const onDateSubmit = (): void => {
    if (isDateValid(keypadInput)) {
      onAccept({
        fieldName: inputName,
        value: getFormattedDate(keypadInput),
      });
    } else {
      onCancel();
    }
  };

  const keypadSubmitButton: IButton = {
    label: 'OK',
    onClick: onDateSubmit,
    testId: 'input-date-required-keypad-button',
    disabled: !canApprove,
  };

  return (
    <>
      <Grid item container direction='column' xs={7} className={classes.inputContent}>
        <Grid item container className={classes.titleContainer}>
          <Grid item className={classes.actionTitle}>
            {caption}
          </Grid>
          <Grid item className={classes.border} />
          <Grid item className={classes.confirmMessage}>
            {message}
          </Grid>
        </Grid>
        <Grid item className={classes.textInputField}>
          <KeypadInputField
            autoFocus
            keyboardId={'input-data'}
            inputFieldLabel={inputLabel}
            itemCode={rifm.value}
            onSubmit={onDateSubmit}
            onChange={rifm.onChange}
            resetKeypad={() => setKeypadInput('')}
            dataTestId={'input-date-required-keypad-input'}
            inputInstructionsLabel={'MM/DD/YYYY'}
          />
        </Grid>
        <Grid item className={classes.cancelContainer}>
          <BaseButton
            buttonType='white'
            className={classes.cancelButton}
            data-testid='input-data-required-cancel-button'
            onClick={onCancel}
          >
            <span className='label'>{t('Cancel_Button')}</span>
          </BaseButton>
        </Grid>
      </Grid>
      <Grid container item xs={5} className={classes.keypadArea}>
        <Grid item container className={classes.bottomContainer}>
          <Grid item className={classes.keypadContainer}>
            <Keypad
              keyboardId={'input-data'}
              visible
              noInputField
              keypadTestIdPrefix={'input-data-'}
              topLabelText={'Enter birth date'}
              submitButton={keypadSubmitButton}
              formatter={RifmOptions.date}
              disableManualWeight
              disableQuantity
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export interface IDateInputProps {
  caption: string;
  message: string;
  isDateOfBirth: boolean;
  onCancel: () => void;
  onAccept: (inputData: IBRMInputDataRequiredInput) => void;
  inputName?: string;
  inputLabel?: string;
}

export default DateInput;
